import React, { ReactNode } from 'react';
import { EnvKind } from '../data/cockpit';
import { ago } from '../data/format';
import { useNow } from '../data/usePoll';

/** The small building blocks every v2 screen is made of; styles in v2.scss. */

const ICONS: Record<string, ReactNode> = {
    dashboard: (
        <>
            <rect x="3" y="3" width="7" height="9" rx="1.5" />
            <rect x="14" y="3" width="7" height="5" rx="1.5" />
            <rect x="14" y="12" width="7" height="9" rx="1.5" />
            <rect x="3" y="16" width="7" height="5" rx="1.5" />
        </>
    ),
    envs: (
        <>
            <rect x="3" y="4" width="18" height="6" rx="1.5" />
            <rect x="3" y="14" width="18" height="6" rx="1.5" />
        </>
    ),
    issues: <path d="M4 6h16M4 12h16M4 18h10" />,
    deploy: <path d="M5 12l4 4 10-10" />,
    exact: <path d="M4 20h16M7 16V9M12 16V5M17 16v-4" />,
    lab: <path d="M9 3h6M10 3v6l-5 9a2 2 0 002 3h10a2 2 0 002-3l-5-9V3" />,
    unknown: (
        <>
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v5M12 16h.01" />
        </>
    ),
    mail: (
        <>
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="M3 7l9 6 9-6" />
        </>
    ),
    search: (
        <>
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" />
        </>
    ),
    pin: (
        <>
            <path d="M12 21s-7-6.2-7-11a7 7 0 0114 0c0 4.8-7 11-7 11z" />
            <circle cx="12" cy="10" r="2.5" />
        </>
    ),
    bell: (
        <>
            <path d="M6 16V11a6 6 0 0112 0v5l2 2H4z" />
            <path d="M10 20a2 2 0 004 0" />
        </>
    ),
    user: (
        <>
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21a8 8 0 0116 0" />
        </>
    ),
    chevron: <path d="M6 9l6 6 6-6" />,
    live: <path d="M3 12h4l3-7 4 14 3-7h4" />,
    log: (
        <>
            <rect x="5" y="3" width="14" height="18" rx="2" />
            <path d="M9 8h6M9 12h6M9 16h4" />
        </>
    ),
    monitor: (
        <>
            <circle cx="12" cy="12" r="3" />
            <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
        </>
    ),
    back: <path d="M15 6l-6 6 6 6" />,
    logout: <path d="M14 4h5v16h-5M10 8l-4 4 4 4M6 12h10" />,
};

export const Icon = ({ name, size = 16 }: { name: keyof typeof ICONS | string; size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
        {ICONS[name]}
    </svg>
);

export const ENV_KIND_LABEL: Record<EnvKind, string> = { prod: 'Productie', test: 'Test', lab: 'Lab' };

export const EnvChip = ({ kind, environment }: { kind: EnvKind; environment: string }) => (
    <span className={`env ${kind}`}>{kind === 'lab' ? `${environment} · lab` : environment}</span>
);

export type Owner = 'us' | 'cu' | 'au';

export const OWNER_LABEL: Record<Owner, string> = { us: 'Wij', cu: 'Klant', au: 'Vanzelf' };

export const OwnerChip = ({ owner, long }: { owner: Owner; long?: boolean }) => (
    <span className={`own ${owner}`}>
        {long ? (owner === 'au' ? 'Vanzelf' : `${OWNER_LABEL[owner]}: actie nodig`) : OWNER_LABEL[owner]}
    </span>
);

export const Card = ({
    title,
    right,
    children,
    className,
    style,
}: {
    title?: ReactNode;
    right?: ReactNode;
    children?: ReactNode;
    className?: string;
    style?: React.CSSProperties;
}) => (
    <section className={`card ${className ?? ''}`} style={style}>
        {(title || right) && (
            <div className="ch">
                {typeof title === 'string' ? <span className="ct">{title}</span> : title}
                {right}
            </div>
        )}
        {children}
    </section>
);

export const Kv = ({ rows, style }: { rows: [ReactNode, ReactNode][]; style?: React.CSSProperties }) => (
    <dl className="kv" style={style}>
        {rows.map(([k, v], i) => (
            <React.Fragment key={i}>
                <dt>{k}</dt>
                <dd>{v ?? '–'}</dd>
            </React.Fragment>
        ))}
    </dl>
);

export type CheckTone = 'ok' | 'bad' | 'warn' | 'wait' | 'todo';

const CHECK_MARK: Record<CheckTone, string> = { ok: '✓', bad: '✕', warn: '!', wait: '›', todo: '' };

export const Mark = ({ tone, children }: { tone: CheckTone; children?: ReactNode }) => (
    <span className={`ic ${tone}`}>{children ?? CHECK_MARK[tone]}</span>
);

export const CheckRow = ({ tone, label, value, mark }: { tone: CheckTone; label: ReactNode; value?: ReactNode; mark?: ReactNode }) => (
    <div className="chk">
        <Mark tone={tone}>{mark}</Mark>
        <span>{label}</span>
        <span className="v">{value}</span>
    </div>
);

export const Meter = ({ pct, tone, width }: { pct: number; tone?: 'w' | 'r' | 'i' | ''; width?: number | string }) => (
    <span className="meter" style={{ width, display: 'inline-block' }}>
        <b className={tone ?? ''} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
    </span>
);

export const Empty = ({ children }: { children: ReactNode }) => <div className="empty">{children}</div>;

export const ErrorNotice = ({ error }: { error?: string }) => (error ? <div className="notice">{error}</div> : null);

/** "3 s" that keeps counting between answers; amber once older than `staleAfter` seconds. */
export const Ago = ({ at, staleAfter }: { at?: string | Date | null; staleAfter?: number }) => {
    const now = useNow(1000);
    if (!at) return <b>–</b>;
    const seconds = (now - new Date(at).getTime()) / 1000;
    return <b className={staleAfter && seconds > staleAfter ? 'warn' : undefined}>{ago(at, now)}</b>;
};

export const Spinner = () => <span className="spin" />;

/** In place of content that has not come in yet, so an empty screen never looks like "nothing there". */
export const Loading = ({ children = 'Laden…' }: { children?: ReactNode }) => (
    <div className="empty loading">
        <Spinner />
        {children}
    </div>
);

/** A row of sources with how fresh each one is. */
export const Freshness = ({ items }: { items: { label: string; at?: string | Date | null; staleAfter?: number }[] }) => (
    <div className="fresh">
        {items.map((i) => (
            <div key={i.label}>
                <span className="mut">{i.label}</span>
                <Ago at={i.at} staleAfter={i.staleAfter} />
            </div>
        ))}
    </div>
);

export const Lamp = ({ tone, size = 14 }: { tone: 'ok' | 'warn' | 'bad' | 'off'; size?: number }) => (
    <span className={`lamp ${tone}`} style={{ width: size, height: size, margin: 0 }} />
);

/** A small line chart of a few values, without axes. */
export const Spark = ({ values, color = '#5C7A96', width = 110, height = 22 }: { values: number[]; color?: string; width?: number; height?: number }) => {
    if (values.length < 2) return <svg width={width} height={height} aria-hidden="true" />;
    const max = Math.max(...values, 1);
    const points = values
        .map((v, i) => `${((i / (values.length - 1)) * 100).toFixed(1)},${(height - 2 - (v / max) * (height - 4)).toFixed(1)}`)
        .join(' ');
    return (
        <svg width={width} height={height} viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" aria-hidden="true">
            <polyline points={points} fill="none" stroke={color} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
        </svg>
    );
};
